import { ReactNode } from "react";
import { MosaicTrafficLightClass } from "../traffic-lights/MosaicTrafficLightClass";
import { useBlockContext } from "./BlockContentProvider";

interface AitLineTrafficLightProps {
  aifid: string;
  top?: string;
}

/**
 * Displays the traffic light for a specific AIB line.
 *
 * @param props - Destructured parameter object
 * @param props.aifid - The block instance id (`aifid`) identifying which line's traffic light to display.
 * @param props.top - Optional CSS top offset used when positioning the traffic light element.
 */
export const AibLineTrafficLight = <T,>({
  aifid,
  top = "-4px",
}: AitLineTrafficLightProps): ReactNode => {
  const { state } = useBlockContext<T>();

  // Check for traffic lights in changedBlocks (existing logic)
  const changedBlockTlValue = state.changedBlocks?.find(
    (b) =>
      b.coreTrafficLight === "RedError" &&
      (b.idAfterTrafficLightIsShown === aifid ||
        (b.idAfterTrafficLightIsShown === "" && aifid === "final")),
  )?.coreTrafficLight;

  // Check for traffic light on the line itself
  const thisLine = state.lines.find((line) => line.aifid === aifid);
  const lineTlValue = thisLine?.coreTrafficLight;

  // Use changedBlocks traffic light if available, otherwise use line traffic light
  const thisTlValue =
    changedBlockTlValue ??
    (lineTlValue === "ChangedUnknown" || lineTlValue === "RedError"
      ? lineTlValue
      : "BlankNotEvaluated");
  const thisTrafficLight =
    !state?.noTrafficLights && thisTlValue !== "BlankNotEvaluated"
      ? new MosaicTrafficLightClass(thisTlValue, top, "-8px").element
      : null;
  return !thisTrafficLight ? null : thisTrafficLight;
};

AibLineTrafficLight.displayName = "AibLineTrafficLight";
